import { useCallback, useMemo, useState } from "react";
import { Button, Em, Flex, Text } from "@radix-ui/themes";
import { PlusIcon } from "@radix-ui/react-icons";
import {
  CreateSampleFormParams,
  CreateSampleFormQuestion,
} from "@/v2/entities/sampling/Sampling.api";
import { Step } from "./Step";
import { QuestionItem } from "./QuestionItem";

export function createQuestion(): CreateSampleFormQuestion {
  return {
    key: `question_${Date.now()}`,
    title: "",
    content: "",
    type: "rating",
    brief: undefined,
    includeSources: false,
  };
}

interface EvaluateStepProps {
  value: CreateSampleFormParams;
  questionInEdit: string | undefined;
  error?: string;
  defaultNewQuestion?: CreateSampleFormQuestion;
  setQuestionInEdit: (key?: string) => void;
  onChange: (value: CreateSampleFormParams) => void;
}

export function EvaluateStep({
  value,
  questionInEdit,
  error,
  defaultNewQuestion,
  setQuestionInEdit,
  onChange,
}: EvaluateStepProps) {
  const [newQuestion, setNewQuestion] = useState<
    CreateSampleFormQuestion | undefined
  >(defaultNewQuestion);

  const allTitles = useMemo(() => {
    return value.questions.map((q) => q.title);
  }, [value.questions]);

  const handleAddQuestion = useCallback(() => {
    const newQuestion = createQuestion();
    setNewQuestion(newQuestion);
    setQuestionInEdit(newQuestion.key);
  }, [onChange, value]);

  const handleQuestionSave = useCallback(
    (key: string, updates: Partial<CreateSampleFormQuestion>) => {
      const newQuestions = value.questions.map((question) =>
        question.key === key ? { ...question, ...updates } : question
      );

      onChange({
        ...value,
        questions: newQuestions,
      });

      setQuestionInEdit(undefined);
    },
    [onChange, value]
  );

  const handleQuestionDelete = useCallback(
    (key: string) => {
      const newQuestions = value.questions.filter((q) => q.key !== key);
      onChange({ ...value, questions: newQuestions });
    },
    [onChange, value]
  );

  const handleSaveNewQuestion = useCallback(
    (key: string, updates: Partial<CreateSampleFormQuestion>) => {
      const newQuestion = createQuestion();

      onChange({
        ...value,
        questions: [...value.questions, { ...newQuestion, ...updates }],
      });
      setNewQuestion(undefined);
    },
    [onChange, value]
  );

  const handleDiscardNewQuestion = useCallback(() => {
    setNewQuestion(undefined);
    setQuestionInEdit(undefined);
  }, []);

  return (
    <Step index={2} label="Evaluate" description="Add evaluation questions">
      <Flex width={"100%"} direction={"column"} gap={"4"}>
        {value.questions.map((question, index) => (
          <QuestionItem
            key={question.key}
            isEditing={questionInEdit === question.key}
            value={question}
            allTitles={allTitles}
            onSave={handleQuestionSave}
            onDiscard={() => setQuestionInEdit(undefined)}
            onDelete={() => handleQuestionDelete(question.key)}
            onStartEditing={() => setQuestionInEdit(question.key)}
          />
        ))}

        {newQuestion != null && (
          <QuestionItem
            key={newQuestion.key}
            isEditing={questionInEdit === newQuestion.key}
            value={newQuestion}
            allTitles={allTitles}
            onSave={handleSaveNewQuestion}
            onDiscard={handleDiscardNewQuestion}
            onDelete={handleDiscardNewQuestion}
            onStartEditing={() => setQuestionInEdit(newQuestion.key)}
          />
        )}

        <Button
          onClick={handleAddQuestion}
          variant="solid"
          color="gray"
          size="2"
          highContrast
        >
          <PlusIcon />
          New Question
        </Button>

        {error && (
          <Text size={"1"} color={"red"}>
            <Em>{error}</Em>
          </Text>
        )}
      </Flex>
    </Step>
  );
}
